/**
 * @file: useErpProviders.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { formatErpProviderTitle } from '@euler/app/flows/erp/utils/formatErpProviderTitle';
import { useCachedQuery } from '@euler/functions/useCachedQuery';
import { ErpIntegrationType } from '@euler/model/enum';
import { ErpProviderInfo } from '@euler/model/erp-integrations';
import { useServiceFactory } from '@euler/services/factory';
import { usePersistFn } from '@euler/utils/hooks';
import { useMemo } from 'react';

const kCacheKey = 'erp-providers';
const kCacheTtl = 24 * 3600 * 1000;

/**
 * The provider catalog, cached on disk. It changes only when the server is
 * deployed, so a stale copy renders immediately and refreshes behind it — no
 * screen waits on the network just to show a provider's name.
 */
export const useErpProviders = () => {
  const { storeErpIntegrationService } = useServiceFactory();

  const { state, loading, error, invalidate } = useCachedQuery<
    ErpProviderInfo[]
  >(
    kCacheKey,
    kCacheTtl,
    async () => await storeErpIntegrationService.getErpProviders(),
  );

  const providers = useMemo(() => state ?? [], [state]);

  const providerMap = useMemo(
    () => new Map(providers.map(p => [p.providerId as string, p])),
    [providers],
  );

  const getProvider = usePersistFn((providerId?: string | null) =>
    providerId ? providerMap.get(providerId) : undefined,
  );

  /**
   * Falls back to the raw id rather than a generic label, so an unknown
   * provider stays identifiable instead of becoming anonymous.
   */
  const getProviderName = usePersistFn(
    (providerId?: string | null, fallback = '当前服务商') => {
      if (!providerId) return fallback;
      return providerMap.get(providerId)?.name ?? providerId;
    },
  );

  /** The company plus its product, for surfaces that name the provider once. */
  const getProviderTitle = usePersistFn(
    (providerId?: string | null, fallback = '当前服务商') => {
      if (!providerId) return fallback;
      const provider = providerMap.get(providerId);
      return provider
        ? formatErpProviderTitle(provider, providerId)
        : providerId;
    },
  );

  const connectableProviders = useMemo(
    () =>
      providers.filter(p =>
        p.supportedIntegrationTypes.includes(ErpIntegrationType.Hosted),
      ),
    [providers],
  );

  return {
    providers,
    /** Only those a store can connect itself from the app. */
    connectableProviders,
    getProvider,
    getProviderName,
    getProviderTitle,
    loading,
    error,
    invalidate,
  };
};
